import moment from 'moment';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { generateClient } from '@aws-amplify/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as icons_solid from '@fortawesome/free-solid-svg-icons';
import { faSort } from '@fortawesome/free-solid-svg-icons';
import { getRecruitmentSources as _getRecruitmentSources } from '@/graphql/queries';
import { getSubjectContacts as _getSubjectContacts } from '@/graphql/small-queries';
import TranscriptModal from '@/TranscriptModal';
import { dash, outcome_color } from '@/utils';
import { engagementSummary } from '@/apis/mockDbCrud';
import useLoadMore from '@/hooks/useLoadMore';
import useSubscription from '@/hooks/useSubscription';
import { buildLocalDateRangeUtcFilter, computeDashboardRange, isWithinInclusiveDateRange } from '@/utils/dashboardDateRange';
import RootContext from '@/RootContext';

const recordingIcon = (<FontAwesomeIcon className="fg-purple" icon={icons_solid.faVolumeLow} size="lg" />);
const transcriptIcon = (<FontAwesomeIcon className="fg-purple" icon={icons_solid.faQuoteLeft} size="lg" />);

const client = generateClient();

const getSubjectContacts = ({ filters, limit, offset, sorting = { field: 'started_at', order: 'desc' } } = {}) => {
  return client.graphql({ query: _getSubjectContacts, variables: { filters, limit, offset, sorting } })
    .then(response => {
      return response.data.getSubjectContacts
    });
};

const NULL_RECRUITMENT_SOURCE = {
    id: null,
    source_name: 'No Recruitment Source'
};

const NULL_CAMPAIGN = {
    id: null,
    source_name: 'No Campaign'
};

const getRecruitmentSources = ({ parent_id } = {}) => {
    const filters = [
        ...(parent_id !== undefined ? [{ parent_id: { eq: parent_id } }] : [])
    ];
    return client.graphql({ query: _getRecruitmentSources, variables: { filters, sorting: { field: 'source_name', order: 'asc' } } })
        .then(response => {
            return [
                NULL_RECRUITMENT_SOURCE,
                ...response.data.getRecruitmentSources.recruitment_sources
            ];
        });
};

const EngagementOutcomesExpanded = () => {
  const isConstructed = useRef(false);
  const rootContext = useContext(RootContext);
	const auth = rootContext.get('auth');
  const location = useLocation();
  const navigate = useNavigate();
  const [hover, setHover] = useState(false);
  const [modal, setModal] = useState();
  const [recruitmentSources, setRecruitmentSources] = useState();
  const [period, setPeriod] = useState(location.state?.selectedPeriod || 'last 30 days');
  const [recruitmentId, setRecruitmentId] = useState();
  const dateRange = useMemo(() => computeDashboardRange(period), [period]);
  const { limit, loadMoreButton, offset, rows, setDefaults, setIsLoadingMore, setRows, setTotal } = useLoadMore();

  const filters_ref = useRef();
  filters_ref.current = useMemo(() => {
    const startedAtFilter = buildLocalDateRangeUtcFilter(dateRange.start, dateRange.end);
    return {
      filters: [{
        ...(startedAtFilter ? { started_at: startedAtFilter } : undefined),
        ...(recruitmentId ? { recruitment_id: { eq: recruitmentId } } : undefined)
      }],
      sorting: { field: 'started_at', order: 'desc' }
    };
  }, [dateRange.end, dateRange.start, recruitmentId]);
  useSubscription({ filters_ref, limit_ref: limit, offset_ref: offset, rankings_key: 'subject_contacts', setRows });

  const load = useCallback(async () => {
    const { filters, sorting } = filters_ref.current;
    const payload = await getSubjectContacts({ filters, limit, offset, sorting });
    const data = payload?.subject_contacts ?? [];
    setRows(prevState => (offset === 0 ? data : [...prevState, ...data]));
    setTotal(payload?.total_count ?? data.length);
    setIsLoadingMore(false);
  }, [filters_ref.current, limit, offset]);

  if (!isConstructed.current) {
    isConstructed.current = true;
    getRecruitmentSources()
      .then(setRecruitmentSources);
  }

  const handleRange = option => {
    setDefaults();
    setPeriod(option);
  };

  const handleRecruitmentId = option => {
    setDefaults();
    setRecruitmentId(option ? parseInt(option, 10) : null);
  };

  useEffect(() => {
    load().catch(console.error);
  }, [load, period, recruitmentId]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => isWithinInclusiveDateRange(row?.started_at, dateRange.start, dateRange.end));
  }, [dateRange.end, dateRange.start, rows]);

  // Sticky header style
  const stickyThStyle = {
    position: "sticky",
    top: 0,
    backgroundColor: '#F4F4F4',
    zIndex: 2,
    fontSize: '0.875rem',
    textAlign: 'left'
  };

  const HeadCell = ({ children, className = '' }) => (
    <th
      style={stickyThStyle}
      className={`py-2 text-start ${className}`}
      scope="col"
    >
      <div className="d-inline-flex align-items-center justify-content-start gap-1">
        <span>{children}</span>
        <FontAwesomeIcon
          icon={faSort}
          style={{ color: '#2E2868', cursor: 'pointer', fontSize: '0.8em' }}
        />
      </div>
    </th>
  );

  const rows_el = useMemo(() => filteredRows.map((e, i) => {
    const onClose = () => {
      setModal();
    };
    const onClick = ({ autoplay = false }) => setModal(<TranscriptModal autoplay={autoplay} id={e.contact_id} onClose={onClose} transcript={e.transcript} />);
    let d = '';
    if (e?.duration_seconds) {
      const _d = moment.duration(e.duration_seconds, 'seconds');
      const h = Math.floor(_d.asHours());
      const m = _d.minutes();
      const s = _d.seconds();
      d = ` ${h > 0 ? String(h).padStart(2, '0') + ':' : ''}${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return (
      <tr key={i} className="border-bottom" style={{ height: '30px' }}>
        <td className="text-start align-middle py-2 ps-3" style={{ borderRight: '1px solid #ccc', paddingRight: '12px' }}>
          <span><b>{moment(e.started_at).format('M/D/YY h:mm:ss A')}</b></span>
          <span>{d}</span>
        </td>
        <td className="text-start align-middle py-2 ps-3" style={{ borderRight: '1px solid #ccc', padding: '0 12px' }}>
          <span className="fw-semibold text-muted">{e.full_name_first}</span>
        </td>
        <td className="text-start align-middle py-2 ps-3" style={{ borderRight: '1px solid #ccc', paddingRight: '12px' }}>
          {e.channel}{auth?.groups?.includes('admin')  && e.ai_platform ? ' / ' + e.ai_platform : ''}
        </td>
        <td className="text-start align-middle py-2 ps-3" style={{ borderRight: '1px solid #ccc', padding: '0 12px' }}>
          <div>
            {(e.outcomes || []).map((outcome, i) => <React.Fragment key={i}><span className={`col-auto mt-1 me-2 p-2 badge ${outcome_color(outcome)}`}>{outcome}</span></React.Fragment>)}
          </div>
        </td>
        <td className="text-start align-middle py-2 ps-3" style={{ padding: '0 12px' }}>
          {e.city || dash}
        </td>
        <td className="text-start align-middle py-2 ps-3" style={{ padding: '0 12px' }}>
          <div className="d-flex justify-content-between align-items-center">
            <span>{e.phone_number}</span>
            <div className="d-flex gap-2 ms-3">
  						{e.recording_key && <div className="col-auto btn-link cursor-pointer" onClick={() => onClick({ autoplay: true })}>{recordingIcon}</div>}
  						<div className="col-auto btn-link cursor-pointer" onClick={onClick}>{transcriptIcon}</div>
            </div>
          </div>
        </td>
      </tr>
    );
  }), [filteredRows]);

  return (
    <div className="container-fluid px-4 pt-5 pb-3 d-flex flex-column" style={{ minHeight: '100vh' }}>
      {modal}
      {/* Header */}
      <div className="d-flex justify-content-between align-items-end mb-3">
        <h5
          className="fw-semibold mb-0 ps-2"
          style={{
            color: "#2E2868",
            fontSize: '1.4375rem',
            lineHeight: '1', // keeps text vertically tight
          }}
        >
          Engagement Summary
        </h5>

        <div className="d-flex align-items-center gap-2">
          <select className="form-select form-select-sm w-auto" value={period} onChange={e => handleRange(e.target.value)}>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last 7 days">Last 7 days</option>
            <option value="last 14 days">Last 14 days</option>
            <option value="last 30 days">Last 30 days</option>
          </select>
          <select className="form-select form-select-sm w-auto" onChange={e => handleRecruitmentId(e.target.value)}>
            {(recruitmentSources || []).map((e, i) => (<option key={i} value={e.id || ''}>{e.source_name}</option>))}
          </select>

          <button
            className="btn btn-sm"
            style={{
              backgroundColor: '#2E2868',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              padding: '6px 12px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
            onClick={() => navigate('/')}
          >
            ✕ Close
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-3 overflow-auto" style={{ maxHeight: "calc(100vh - 150px)" }}>
        <table
          className="table table-sm table-borderless align-middle mb-0"
          style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}
        >
          <thead className="text-muted small" style={{ height: "30px" }}>
            <tr className="border-bottom">
              <HeadCell className="text-start align-middle py-2 ps-3">Date & Time</HeadCell>
              <HeadCell className="text-start align-middle py-2 ps-3">Subject</HeadCell>
              <HeadCell className="text-start align-middle py-2 ps-3">Channel</HeadCell>
              <HeadCell className="text-start align-middle py-2 ps-3">Outcomes</HeadCell>
              <HeadCell className="text-start align-middle py-2 ps-3">City</HeadCell>
              <HeadCell className="text-start align-middle py-2 ps-3">Phone Number</HeadCell>
            </tr>
          </thead>

          <tbody>
            {rows_el}
          </tbody>
        </table>
      </div>
      {loadMoreButton}
    </div>
  );
};

export default EngagementOutcomesExpanded;
